<script setup lang="ts">
import { ref, watch } from "vue";

import Button from "@/dsComponents/buttons/Button.vue";

import Overlay from "./Overlay.vue";

interface SideModalProps {
    open: boolean;
    width?: number;
    title?: string;
    close?: boolean;
    padding?: number;
}
const props = withDefaults(defineProps<SideModalProps>(), { open: false, width: 500, close: true, padding: 40 });

const emit = defineEmits(["close"]);

const closeAnimate = ref(false);

const closeFn = async () => {
    closeAnimate.value = true;

    setTimeout(() => {
        emit("close");
        closeAnimate.value = false;
        isOpen.value = false;
    }, 500);
};

const isOpen = ref(false);
watch(
    () => props.open,
    () => {
        if (props.open) isOpen.value = props.open;
        else closeFn();
    },
    { immediate: true }
);
</script>
<template>
    <Teleport to="#teleport-modal">
        <Overlay :open="isOpen" v-show="isOpen" :fadeOut="closeAnimate" :center="false" @click="closeFn">
            <div
                class="ds-side-modal-component"
                :class="{ open, close: closeAnimate }"
                :style="`--side-modal-width: ${width}px; --side-modal-padding: ${padding}px`"
            >
                <div class="wrapper" :style="`--side-modal-padding: ${padding}px`">
                    <div class="ds-side-modal-container">
                        <div class="modal-header">
                            <div v-if="title" class="modal-title">{{ title }}</div>
                            <div class="modal-actions">
                                <slot name="header-actions"></slot>
                                <Button class="close-button" v-if="close" icon-only="x" size="sm" type="neutral" variant="stroke" @click="closeFn" />
                            </div>
                        </div>
                        <slot></slot>
                    </div>
                </div>
                <div v-if="$slots['footer-actions']" class="footer-actions">
                    <slot name="footer-actions"></slot>
                </div>
            </div>
        </Overlay>
    </Teleport>
</template>
<style scoped lang="scss">
@keyframes modalSlideIn {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(calc(var(--side-modal-width) * -1));
    }
}
@keyframes modalSlideOut {
    from {
        transform: translateX(calc(var(--side-modal-width) * -1));
    }

    to {
        transform: translateX(0);
    }
}

.ds-side-modal-component {
    width: var(--side-modal-width);
    height: 100%;
    background-color: white;
    position: fixed;
    right: calc(var(--side-modal-width) * -1);
    top: 0;
    z-index: 999998;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .wrapper {
        padding: var(--side-modal-padding);
        overflow-y: auto;

        .ds-side-modal-container {
            position: relative;
            .modal-header {
                display: flex;
                justify-content: space-between;
                margin-bottom: 24px;

                .modal-title {
                    @include font-size-18;
                    font-weight: 600;
                    color: $text-main-900;
                    flex-shrink: 0;
                    max-width: 90%;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    align-content: center;
                }

                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: var(--spacing-xxs, 2px);
                    // Override legacy style
                    padding: 0;
                    width: 100%;
                }
            }
        }
    }

    .footer-actions {
        border-top: solid $neutral-200 1px;
        gap: 12px;
        bottom: 0;
        width: 100%;
        left: 0;
        padding: 24px 40px;
        background-color: $white;
        display: flex;
        justify-content: space-between;
        align-items: center;

        z-index: 999999;

        div {
            flex-grow: 1;
        }
    }

    &.open {
        animation: modalSlideIn forwards 0.5s;
    }

    &.close {
        animation: modalSlideOut forwards 0.5s;
    }
}
</style>
<style lang="scss">
.ds-side-modal-component {
    .footer-actions {
        * {
            flex-grow: 1;
        }
    }
}
</style>
